// src/app/pages/admin/users/user-edit-create/user-edit-create.component.ts
// noinspection DuplicatedCode
import { Component, OnInit } from '@angular/core';
import {
  FormsModule, ReactiveFormsModule, UntypedFormBuilder,
  UntypedFormGroup, Validators
} from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';

import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { FileUploadComponent } from '@shared/components/file-upload/file-upload.component';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';

import { UserDetails } from '@core/models/UserDetails';
import { Settlement } from '@core/models/Settlement';
import { BusinessGroup } from '@core/models/BusinessGroup';
import { Branch } from '@core/models/Branch';
import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import { CreateUserResponse } from '@core/models/Http/Response/CreateUserResponse';
import { ImageSignature } from '@core/models/ImageSignature';
import { CreateEditService } from '@core/http/create-edit-service';
import { CatalogsService } from '@core/http/catalogs.service';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';
import { AuthManagementService } from '@core/service/auth-management.service';
import { UserRole } from '@core/models/Enums/UserRole';
import { REGEX } from '@shared/utils/regex.constants';
import { MsgResponse } from '@core/models/MsgResponse';
import Swal from 'sweetalert2';
import { environment } from '@enviroment/environment';
import { UsuarioAssetPayload, UsuarioAssetUpsert } from '@core/models/UsuarioAsset';

// Tipos de usuario (catalogo)
import { UserType } from '@core/models/UserType';
import {UserService} from "@core/http/table-data-services/User.service";
import {RegionWordPipe} from "@core/pipes/region-word.pipe";
import {regionWord} from "@shared/utils/region-word.util";

@Component({
  selector: 'app-user-edit-create',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    FormsModule, ReactiveFormsModule,
    MatButton, MatFormField, MatIcon, MatInput, MatLabel, MatOption, MatSelect,
    NgForOf, NgClass, MatAutocompleteTrigger, MatAutocomplete, NgIf,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, MatDatepickerModule, MatButtonModule,
    FileUploadComponent, NgxMatIntlTelInputComponent, RegionWordPipe
  ],
  templateUrl: './user-edit-create.component.html',
  styleUrl: './user-edit-create.component.scss',
})
export class UserEditCreateComponent implements OnInit {
  param: string | null = null;      // idUsuario (base64) si es edicion
  paramGid: string | null = null;   // idGEMP (base64) si aplica

  /** Opciones del select con GUID real */
  roleOptions: UserType[] = [];
  /** Catalogo completo (sin filtros) */
  private tiposUsuarioAll: UserType[] = [];

  user: UserDetails;
  archive: ImageSignature;

  userImagen = 'assets/images/user/imgUSer.png';
  private readonly EMPLEADO_FALLBACK = 'assets/images/empleado.png';

  businessGroups: BusinessGroup[] = [];
  branches: Branch[] = [];

  settlements: Settlement[] = [];
  settlementSearch = '';

  userForm: UntypedFormGroup;
  archiveForm: UntypedFormGroup;
  preferredCountries: string[] = ['mx', 'es', 'us'];

  breadcrumbItems = [
    { label: 'PERSONAL', link: '/admin/user-list' },
    { label: 'NUEVO PERSONAL' }
  ];

  // flags
  isSupervisor = false;
  isResponsable = false;

  // resumen/submit
  submitted = false;

  constructor(
    private catalogsService: CatalogsService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private ecService: CreateEditService,
    private httpMsgHandler: HttpMesageHandlerService,
    private authManagement: AuthManagementService,
    private userService: UserService
  ) {
    this.user = new UserDetails({} as UserDetails);
    this.archive = new ImageSignature({} as ImageSignature);

    this.route.paramMap.subscribe(params => {
      const param = params.get('param');
      this.param = param ? atob(param) : null;
      const gid = params.get('gid');
      this.paramGid = gid ? atob(gid) : null;
    });

    // CREATE: no incluimos idUsuario en el form
    this.userForm = this.createUserForm(false);

    this.archiveForm = this.createArchivesForm();

    const userDetails = this.authManagement.userData();
    this.isResponsable = userDetails.TipoUsuario === UserRole.Responsable_Farmacia;
    this.isSupervisor  = userDetails.TipoUsuario === UserRole.Supervisor_Sucursales;

    const r = (environment as any)?.region?.toString().toUpperCase();
    this.preferredCountries = (r === 'ES') ? ['es','mx','us'] : ['mx','es','us'];
  }

  /** Normaliza para comparar nombres de roles del backend contra el enum */
  private normalizeName(s: string | null | undefined): string {
    return (s ?? '').toString().trim().toLowerCase();
  }

  /** Lista blanca (coincide con tu enum). Se ignoran Particular y SysUser */
  private readonly ALLOWED_NAMES = new Set<string>([
    this.normalizeName(UserRole.Admin),                 // "Super Admin"
    this.normalizeName(UserRole.Medico),                // "Medico"
    this.normalizeName(UserRole.Responsable_Farmacia),  // "Responsable Farmacia"
    this.normalizeName(UserRole.Paciente),              // "Paciente"
    this.normalizeName(UserRole.Empleado_Farmacia),     // "Empleado Farmacia"
    this.normalizeName(UserRole.Supervisor_Sucursales), // "Supervisor Sucursales"
  ]);

  /** Nunca ofrecer estos tipos en CREAR */
  private readonly EXCLUDED_ALWAYS_ON_CREATE = new Set<string>([
    this.normalizeName(UserRole.Admin), // "Super Admin"
    // agrega aqui si quieres excluir mas en CREAR:
    // this.normalizeName(UserRole.Medico),
    // this.normalizeName(UserRole.Paciente),
  ]);

  /** Reglas de que puede crear cada rol logueado */
  private computeCreatableNamesFor(logged: UserRole): string[] {
    if (logged === UserRole.Admin) {
      return [UserRole.Supervisor_Sucursales, UserRole.Empleado_Farmacia, UserRole.Responsable_Farmacia];
    }
    if (logged === UserRole.Supervisor_Sucursales) {
      return [UserRole.Empleado_Farmacia, UserRole.Responsable_Farmacia];
    }
    if (logged === UserRole.Responsable_Farmacia) {
      return [UserRole.Empleado_Farmacia];
    }
    return [];
  }

  formatRoleName(name: string): string {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  ngOnInit(): void {
    // Admin: cargar grupos
    if (!this.isSupervisor && !this.isResponsable) {
      this.getBusinessGroups();
    }

    // Supervisor: fijar GEMP y cargar sus sucursales
    if (this.isSupervisor) {
      const u = this.authManagement.userData();
      this.userForm.patchValue({ idGEMP: u.IdGEMP }, { emitEvent: false });
      this.getBranchesByGemp(u.IdGEMP);
    }

    // Responsable: fijar GEMP+Sucursal y ocultar seccion
    if (this.isResponsable) {
      const u = this.authManagement.userData();
      this.userForm.patchValue({ idGEMP: u.IdGEMP, idSucursal: u.IdSucursal }, { emitEvent: false });
    }

    if (this.param) {
      // EDITAR: cargar datos y reconstruir form con idUsuario
      this.getUserdetails(this.param, this.paramGid);
      this.setBreadcrumb();
    }

    // Cargar tipos de usuario
    this.loadTiposUsuario();

    // CP/colonia
    this.userForm.get('settlementSearch')?.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap((value: string | Settlement) => {
          const v = value as any;
          if (typeof v === 'string' && v.length === 5 && /^\d+$/.test(v)) {
            this.searchSettlements(v);
          } else if (!v || typeof v !== 'object') {
            this.settlements = [];
          }
        })
      )
      .subscribe();
  }

  // Pega esto en tu componente user-edit-create.component.ts

  get resolvedPanelImage(): string {
    // 1) intentar tomar la imagen del form (puede ser string o UsuarioAssetPayload)
    const formVal: any = this.archiveForm?.get('imagen')?.value ?? null;
    const fromForm = this.resolveImageAnyToDataUrl(formVal);

    // 2) si no hay en form, usar la ya cargada del backend (this.userImagen)
    const candidate = fromForm || this.userImagen;

    // 3) fallback
    const s = (candidate || '').toString().trim();
    return s ? s : this.EMPLEADO_FALLBACK;
  }

  /** Acepta string | null | undefined | UsuarioAssetPayload y devuelve un dataURL usable */
  private resolveImageAnyToDataUrl(v: any): string {
    if (!v) return '';

    // Caso 1: ya es string
    if (typeof v === 'string') {
      const s = v.trim();
      if (!s || s === 'null' || s === 'undefined') return '';
      if (s.startsWith('data:image/')) return s;               // dataURL completa
      if (s.startsWith('http') || s.startsWith('assets/')) return s; // url
      try {
        // si es base64 "crudo" (sin encabezado), validamos que decodifique
        atob(s);
        return `data:image/png;base64,${s}`;
      } catch {
        return '';
      }
    }

    // Caso 2: objeto (UsuarioAssetPayload)
    // intenta en este orden: dataUrl/dataURL/base64
    const raw = v.dataUrl ?? v.dataURL ?? v.base64 ?? '';
    if (typeof raw !== 'string' || !raw) return '';

    if (raw.startsWith('data:image/')) return raw; // ya viene con encabezado

    const ct = v.contentType || 'image/jpeg';
    return `data:${ct};base64,${raw}`;
  }


  private setBreadcrumb(): void {
    this.breadcrumbItems = [
      { label: 'PERSONAL', link: '/admin/user-list' },
      { label: this.param ? 'EDITAR PERSONAL' : 'NUEVO PERSONAL' }
    ];
  }

  // ===================== CATALOGOS =====================

  private loadTiposUsuario(): void {
    const logged = this.authManagement.userData()?.TipoUsuario as UserRole;

    this.userService.getTiposUsuario().subscribe({
      next: (res: DefaultResponse<UserType[]>) => {
        const data = (res.data || []).map(t => new UserType(t as any));

        // 1) Filtra por lista blanca (case-insensitive)
        this.tiposUsuarioAll = data.filter(t =>
          this.ALLOWED_NAMES.has(this.normalizeName(t.nombre))
        );

        // 2) Aplica reglas de creacion segun logueado
        const creatables = this.computeCreatableNamesFor(logged).map(n => this.normalizeName(n));

        // 3) Base: solo los creables
        let base = this.tiposUsuarioAll.filter(t =>
          creatables.includes(this.normalizeName(t.nombre))
        );

        // 4) CREAR: nunca mostrar los excluidos (ej. Super Admin)
        if (!this.param) {
          base = base.filter(t => !this.EXCLUDED_ALWAYS_ON_CREATE.has(this.normalizeName(t.nombre)));
        }

        // 5) EDITAR: incluir el tipo actual aunque no sea “creable”
        const idTipoActual = this.user?.idTipoUsuario;
        if (this.param && idTipoActual) {
          const actual = this.tiposUsuarioAll.find(t => String(t.idTipoUsuario) === String(idTipoActual));
          if (actual && !base.some(b => b.idTipoUsuario === actual.idTipoUsuario)) {
            base = [actual, ...base];
          }
        }

        // 6) Ordena y asigna
        this.roleOptions = base.sort((a, b) => a.nombre.localeCompare(b.nombre));

        // 7) Si el valor actual ya no existe tras el filtro, limpiarlo
        const currentId = this.userForm.get('idTipoUsuario')?.value;
        if (currentId && !this.roleOptions.some(r => String(r.idTipoUsuario) === String(currentId))) {
          this.userForm.patchValue({ idTipoUsuario: null }, { emitEvent: false });
        }
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  getBusinessGroups() {
    this.catalogsService.getBusinessGroups().subscribe({
      next: (res: DefaultResponse<BusinessGroup[]>) => this.businessGroups = [...res.data],
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  getBranchesByGemp(idGEMP = '') {
    if (!idGEMP) idGEMP = this.userForm.get('idGEMP')?.value;
    if (!idGEMP) { this.branches = []; return; }
    this.catalogsService.getBranchesByIdGEMP(idGEMP).subscribe({
      next: (res: DefaultResponse<Branch[]>) => this.branches = [...res.data],
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  // ===================== EDICION / MAPEO =====================

  removeAccents(value: string): string {
    return value ? value.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : value;
  }

  // Reemplaza COMPLETA esta funcion en:
// src/app/pages/admin/users/user-edit-create/user-edit-create.component.ts

  getUserdetails(paramUsr: string, _paramGid: string | null) {
    // ahora paramUsr es el USR y se consulta directo al nuevo endpoint
    this.ecService.getUserByUsr(paramUsr).subscribe({
      next: (res: DefaultResponse<UserDetails | UserDetails[]>) => {
        // Soporta {data: obj} | {data: []} | obj directo
        const data: any = (res && 'data' in res) ? (res as any).data : res;
        const u: any = Array.isArray(data) ? (data[0] ?? null) : (data ?? null);

        if (!u) {
          this.httpMsgHandler.handleMsg({
            code: 0,
            message: 'No se encontro el usuario con el USR especificado.',
            toast: 'warning'
          } as any);
          return;
        }

        // nombre del asentamiento puede venir con distintos keys
        this.settlementSearch = u.nombreAsentamiento ?? u.asentamiento ?? '';

        // normaliza correo por si cambia el nombre del campo
        const correo = u.email ?? u.correo ?? u.eMail ?? '';
        u.email = this.removeAccents(correo);

        // dispara la busqueda de colonias para preseleccionar la actual
        this.searchSettlements(this.settlementSearch);

        // mapeo al modelo local
        this.user = this.mapToUserDetails(u);

        // EDITAR: reconstruye form con idUsuario incluido
        this.userForm = this.createUserForm(true);

        // imagen base64 si viene
        if (u.imagen) {
          this.userImagen = 'data:image/png;base64,' + u.imagen;
        }

        // carga sucursales del GEMP actual
        const g = this.user.idGEMP || this.userForm.get('idGEMP')?.value;
        if (g) this.getBranchesByGemp(g);

        // recargar catalogo ya con idTipoUsuario del usuario
        this.loadTiposUsuario();
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }


  mapToUserDetails(r: any): UserDetails {
    this.getBranchesByGemp(r.idGEMP);
    const u = new UserDetails({} as UserDetails);
    u.idUsuario = r.idUsuario;
    u.nombres = r.nombres;
    u.primerApellido = r.primerApellido;
    u.segundoApellido = r.segundoApellido;
    u.movil = r.movil;
    u.email = r.email;
    u.domicilio = r.domicilio;
    u.idAsentamiento = r.idAsentamiento;
    u.nombreAsentamiento = r.asentamiento;
    u.idCP = r.idCP;
    u.codigoPostal = r.codigoPostal;
    u.idMunicipio = r.idMunicipio;
    u.municipio = r.municipio;
    u.idGEMP = r.idGEMP;
    u.usr = r.usr;
    u.idSucursal = r.idSucursal;
    u.idTipoUsuario = r.idTipoUsuario;
    return u;
  }

  // ===================== CP / colonia =====================

  searchSettlements(cp: string) {
    if (!cp) { this.settlements = []; return; }
    this.catalogsService.getSettlementByPostalCode(cp).subscribe({
      next: (res: DefaultResponse<Settlement[]>) => {
        this.settlements = [...res.data];
        if (this.settlements.length === 0) {
          this.userForm.patchValue({ settlementSearch: null }, { emitEvent: false });
        }
        const idAct = this.userForm.get('idAsentamiento')?.value || this.user.idAsentamiento;
        if (idAct) {
          const sel = this.settlements.find(s => s.idAsentamiento === idAct);
          if (sel) {
            this.userForm.patchValue({ settlementSearch: sel, idAsentamiento: sel.idAsentamiento }, { emitEvent: false });
          }
        }
      },
      error: (err) => this.httpMsgHandler.handleMsg(err)
    });
  }

  onSettlementBlur() {
    const val = this.userForm.get('settlementSearch')?.value;
    if (!val || typeof val !== 'object' || !val.idAsentamiento) {
      this.userForm.patchValue({ settlementSearch: null }, { emitEvent: false });
    }
  }

  displayFn(s: Settlement): string { return s && s.asentamiento ? s.asentamiento : ''; }
  setSelectedSettlement(_: Settlement): void {}

  // ===================== forms =====================

  // Crea el form sin idUsuario en CREATE y con idUsuario en EDIT
  private createUserForm(includeId: boolean = false): UntypedFormGroup {
    const group: any = {
      usr: [this.user.usr, [Validators.required, Validators.pattern(REGEX.user)]],
      password: [null],
      idTipoUsuario: [this.user.idTipoUsuario, Validators.required], // GUID
      idGEMP: [this.user.idGEMP],
      idSucursal: [this.user.idSucursal],
      nombres: [this.user.nombres, [Validators.required, Validators.pattern(REGEX.text)]],
      primerApellido: [this.user.primerApellido, [Validators.required, Validators.pattern(REGEX.text)]],
      segundoApellido: [this.user.segundoApellido, [Validators.required, Validators.pattern(REGEX.text)]],
      domicilio: [this.user.domicilio, [Validators.required]],
      movil: [this.user.movil, [Validators.required]],
      email: [this.user.email, [Validators.required, Validators.pattern(REGEX.correo)]],
      idAsentamiento: [this.user.idAsentamiento],
      settlementSearch: [null, Validators.required],
    };

    if (includeId) {
      group.idUsuario = [this.user.idUsuario]; // solo en EDIT
    }
    return this.fb.group(group);
  }

  private createArchivesForm(): UntypedFormGroup {
    return this.fb.group({
      idUsuario: [this.archive.idUsuario],
      imagen: [this.archive.imagen], // aqui el uploader pone UsuarioAssetPayload
      firma: [this.archive.firma],
      pdf: [this.archive.pdf],
    });
  }

  private markAllTouched(): void {
    Object.values(this.userForm.controls).forEach(c => c.markAsTouched());
  }

  // ===================== guardar =====================
  private getIdUsuarioFromSaveResponse(
    res: DefaultResponse<CreateUserResponse | string | any>
  ): string {
    // create => backend puede regresar el UUID en string, o en data.idUsuario
    if (res?.data) {
      if (typeof res.data === 'string') return res.data;
      if (typeof res.data === 'object' && res.data.idUsuario) return String(res.data.idUsuario);
    }
    // si no viene en la respuesta (caso edicion), usamos el que ya teniamos cargado
    return this.user?.idUsuario ?? '';
  }

  public confirmAdd(): void {
    this.submitted = true;

    const sel = this.userForm.get('settlementSearch')?.value;
    if (!sel || typeof sel !== 'object' || !sel.idAsentamiento) {
      this.userForm.patchValue({ settlementSearch: null }, { emitEvent: false });
    }

    if (this.userForm.invalid) {
      this.markAllTouched();
      return;
    }

    const userData = this.userForm.getRawValue();

    const selectedSettlement = userData.settlementSearch as Settlement;
    if (!selectedSettlement || !selectedSettlement.idAsentamiento) {
      Swal.fire({
        icon: 'error',
        title: regionWord('Colonia requerida'),
        text: regionWord('Debes seleccionar una colonia de la lista para continuar.'),
      }).then();
      return;
    }

    // mapear CP/colonia
    userData.idAsentamiento = selectedSettlement.idAsentamiento;
    delete userData.settlementSearch;

    // CREATE: borrar idUsuario del payload si por alguna razon viniera
    if (!this.param && 'idUsuario' in userData) {
      delete (userData as any).idUsuario;
    }

    userData.password = null;

    // normaliza telefono (E.164 si viene del control)
    const rawMovil = this.userForm.get('movil')?.value;
    let movilE164 = '';
    if (typeof rawMovil === 'string') movilE164 = rawMovil;
    else if (rawMovil?.e164 || rawMovil?.e164Number) movilE164 = rawMovil.e164 || rawMovil.e164Number;
    else if (rawMovil?.number) movilE164 = rawMovil.number;
    userData.movil = (movilE164 || '').trim();

    // === Guardar usuario (create/edit) ===
    this.userService.createUpdateUser(userData).subscribe({
      next: (res: DefaultResponse<CreateUserResponse | string | any>) => {
        // ⚠️ Ya NO usamos this.param aqui (porque trae USR en edicion).
        // Tomamos el id de la respuesta; si no viene (caso edicion), usamos el cargado en this.user.idUsuario
        const idUsuario = this.getIdUsuarioFromSaveResponse(res);

        if (!idUsuario) {
          this.httpMsgHandler.handleMsg({
            code: 0,
            message: 'No se obtuvo idUsuario del guardado.',
            toast: 'error'
          } as any);
          return;
        }

        // ====== SUBIR/ACTUALIZAR ASSET (POST JSON con blobBase64) ======
        const imgVal = this.archiveForm.get('imagen')?.value as UsuarioAssetPayload | null;

        // helper para remover prefijo "data:image/..;base64,"
        const toPlainBase64 = (v?: string | null) => {
          if (!v) return '';
          const i = v.indexOf('base64,');
          return i >= 0 ? v.substring(i + 'base64,'.length) : v;
        };

        if (imgVal && (imgVal.base64 || imgVal.dataUrl)) {
          const upsert: UsuarioAssetUpsert = {
            idUsuario,                // ✅ ahora es el UUID correcto, nunca el USR
            tipo: 1,                  // 1 = FOTO USUARIO (ajusta si tu catalogo usa otro valor)
            blobBase64: toPlainBase64(imgVal.base64 ?? imgVal.dataUrl ?? ''),
            contentType: imgVal.contentType || 'image/jpeg',
            bytesSize: Number(imgVal.bytesSize || 0),
            widthPx: imgVal.widthPx ?? null,
            heightPx: imgVal.heightPx ?? null,
            hashSha256Base64: imgVal.hashSha256Base64 ?? null,
          };

          if (upsert.blobBase64 && upsert.blobBase64.length > 0) {
            this.userService.uploadUsuarioAsset(upsert).subscribe({
              error: (err) => this.httpMsgHandler.handleMsg(err),
            });
          }
        }

        this.httpMsgHandler.handleMsg(res as MsgResponse);
        this.router.navigate(['admin/user-list']).then();
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
    });
  }

}
